import { ReactNode, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useLogout } from '@/hooks/useAuth';

interface NavItem {
  to: string;
  labelKey: string;
}

/**
 * PatronEZ admin shell. Mirrors AppShell's hairline aesthetic but distinguishes
 * itself with a darker rail + an "Admin" badge in the top bar — operators
 * should never confuse this with a normal workspace view.
 *
 * Gating: redirects to /app if `me.role` is anything other than `owner|admin`.
 */
export function AdminShell({ titleKey, children }: { titleKey: string; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const me = useCurrentUser();
  const logout = useLogout();

  useEffect(() => {
    if (me.isLoading) return;
    const role = me.data?.role;
    if (!me.data || (role !== 'owner' && role !== 'admin')) {
      navigate('/app', { replace: true });
    }
  }, [me.data, me.isLoading, navigate]);

  const items: NavItem[] = [
    { to: '/admin', labelKey: 'admin.nav.stats' },
    { to: '/admin/users', labelKey: 'admin.nav.users' },
    { to: '/admin/companies', labelKey: 'admin.nav.companies' },
    { to: '/admin/ai-runs', labelKey: 'admin.nav.ai_runs' },
    { to: '/admin/ai-spend', labelKey: 'admin.nav.ai_spend' },
    { to: '/admin/audit', labelKey: 'admin.nav.audit' },
  ];

  return (
    <div className="min-h-screen flex bg-ink-50">
      <aside className="w-56 shrink-0 border-r border-ink-200 bg-white flex flex-col">
        <div className="px-5 py-4 border-b border-ink-200">
          <Link to="/app" className="flex items-center gap-2 text-ink-900 font-semibold">
            <img src="/logo.png" alt="" width={18} height={18} />
            <span className="serif-jp text-base">{t('brand.name')}</span>
            <span className="ml-1 inline-flex items-center rounded-sm border border-ink-300 px-1.5 py-0 text-[10px] font-medium text-ink-600 uppercase tracking-wider">
              {t('admin.badge')}
            </span>
          </Link>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5 text-sm">
          {items.map((it) => {
            const active = location.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                className={
                  'block px-3 py-1.5 rounded-md transition ' +
                  (active
                    ? 'bg-accent-50 text-accent-700 font-medium'
                    : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900')
                }
              >
                {t(it.labelKey)}
              </Link>
            );
          })}
          <div className="pt-3 mt-3 border-t border-ink-200">
            <Link
              to="/app"
              className="block px-3 py-1.5 rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            >
              ← {t('admin.back_to_app')}
            </Link>
          </div>
        </nav>
        <div className="border-t border-ink-200 px-3 py-3 text-xs text-ink-600 space-y-2">
          <div className="font-medium truncate">{me.data?.name || me.data?.email}</div>
          <button onClick={logout} className="block text-ink-500 hover:text-ink-900">
            {t('common.logout')}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-auto">
        <header className="border-b border-ink-200 bg-white">
          <div className="px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-semibold text-ink-900">{t(titleKey)}</h1>
              <span className="inline-flex items-center rounded-sm bg-ink-900 px-2 py-0.5 text-[10px] font-medium text-white uppercase tracking-wider">
                {t('admin.badge')}
              </span>
            </div>
            <div className="text-sm text-ink-500">{me.data?.name || me.data?.email}</div>
          </div>
        </header>
        <div className="px-8 py-6">{children}</div>
      </main>
    </div>
  );
}
